/**
 * BeltRowCard — one rule-surfaced attention row on the Operations Surface belt
 * (OAS). Not a Task: no expand, no thread. It wears a red (overdue) / amber (due
 * today) left stripe driven by `dueBy`, a severity pill, and the matter's Pin
 * toggle. The row is for awareness and carries no action (#969 removed Nudge
 * firm, Got it, Snooze and Confirm); it leaves the belt when its rule stops
 * matching.
 */

import type { DashboardRow, SurfacePin } from "@command-center/shared";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { PinToggle } from "@/components/pin-toggle";

/** Whole days from `dueBy` to `today` (both YYYY-MM-DD). Clamps negatives to 0. */
function overdueDays(dueBy: string, today: string): number {
  const due = Date.parse(`${dueBy}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(due) || Number.isNaN(now)) return 0;
  const days = Math.floor((now - due) / 86_400_000);
  return days < 0 ? 0 : days;
}

export function BeltRowCard({
  row,
  today,
  pinned,
  recordLine,
}: {
  row: DashboardRow;
  today: string;
  /** The viewer's current pins — to show whether this row's matter is already
   *  pinned and to un-pin it. #471 Slice 1. */
  pinned: SurfacePin[];
  /** Overrides the secondary line. In the by-status belt the line is the firm
   *  (`row.instruction`); in the by-firm subtab the firm is the section header,
   *  so the criterion is passed here to take its place. */
  recordLine?: string;
}) {
  const overdue = row.dueBy !== null && row.dueBy < today;
  const days = row.dueBy !== null ? overdueDays(row.dueBy, today) : 0;
  const label = overdue
    ? days <= 1
      ? "Overdue 1 day"
      : `Overdue ${days} days`
    : "Due today";

  return (
    <Card
      className={cn(
        "text-sm",
        // Left scan-lane (Q-SEMANTIC): severity on the row's left edge — the
        // scan companion to the labelled pill below. Same --sev-* tokens as the
        // pill so the two match and theme-flip (was raw red-600/amber-500,
        // off-token and a hair off the pill's hue).
        overdue
          ? "border-l-4 border-l-[hsl(var(--sev-red))]"
          : "border-l-4 border-l-[hsl(var(--sev-amber))]",
      )}
      data-testid={`belt-row-${row.id}`}
      data-kind="belt"
    >
      <div className="flex items-start gap-3 p-3">
        <div className="min-w-0 flex-1 space-y-0.5">
          <div
            className="truncate text-[15px] font-medium leading-snug text-foreground"
            data-testid={`belt-row-title-${row.id}`}
          >
            {row.title}
          </div>
          {(() => {
            const line = recordLine ?? row.instruction;
            return line !== null && line.trim() !== "" ? (
              <p className="text-[13px] text-muted-foreground">{line}</p>
            ) : null;
          })()}
        </div>
        <span
          className={cn(
            "shrink-0 self-center rounded-full px-2 py-0.5 text-[11px] font-semibold",
            // Time-severity = PRIORITY (Q-SEMANTIC), the same channel as the
            // dashboard's one attention chip (ADR-0104) — solid traffic fill +
            // navy ink, NOT the feedback tokens (this is a matter's urgency, not
            // a system message).
            overdue
              ? "bg-[hsl(var(--sev-red))] text-[hsl(var(--sev-ink))]"
              : "bg-[hsl(var(--sev-amber))] text-[hsl(var(--sev-ink))]",
          )}
          data-testid={`belt-row-attention-${row.id}`}
        >
          {label}
        </span>
        {/* #471 Slice 1: the one-click matter pin — shared PinToggle renders
            nothing for firm-level / task rows (leadId null). */}
        <PinToggle
          leadId={row.leadId}
          pins={pinned}
          className="shrink-0 self-center"
          testId={`belt-row-pin-${row.id}`}
        />
      </div>
    </Card>
  );
}
